import { AdvancedEventEmitter, EventBroker, IEventBroker, wait } from '@zdrbm/zdr-native-tools';
import { CaptureScreenResult, IScreenshotService, ScreenshotStatus } from './IScreenshotService';
import { IImageFactory } from '../imageService';

export class BrowserScreenshotService extends AdvancedEventEmitter implements IScreenshotService {
    takingScreenshot: IEventBroker;
    screenshotTaken: IEventBroker;

    constructor(private imageFactory: IImageFactory) {
        super();
        this.takingScreenshot = new EventBroker(this);
        this.screenshotTaken = new EventBroker(this);
    }

    async captureScreen(): Promise<CaptureScreenResult> {
        let captureResult = {} as CaptureScreenResult;
        try {
            const mediaStream: MediaStream = await navigator.mediaDevices.getDisplayMedia({
                // @ts-ignore
                preferCurrentTab: true,
                video: { frameRate: 30 },
            });
            this.takingScreenshot.emit({});
            await this.waitForFocus(mediaStream);

            const video = this.createVideoElementToCaptureFrames(mediaStream);
            await new Promise<void>((resolve) => {
                video.addEventListener('loadedmetadata', () => {
                    video.play().then(resolve);
                });
            });

            const image = this.imageFactory.fromVideoElement(video);

            this.stopCapture(video);

            captureResult = { image, status: ScreenshotStatus.SUCCESS };
        } catch (error) {
            const err = error as Error;
            captureResult.error = err.message;
            if (err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
                captureResult.status = ScreenshotStatus.PERMISSION_DENIED;
                return captureResult;
            }
            captureResult.status = ScreenshotStatus.ERROR;

            return captureResult;
        } finally {
            this.screenshotTaken.emit({});
        }

        return captureResult;
    }

    private async waitForFocus(result: MediaStream): Promise<MediaStream> {
        await wait(300);
        if (document.hasFocus()) {
            return result;
        }
        return this.waitForFocus(result);
    }

    private createVideoElementToCaptureFrames(mediaStream: MediaStream): HTMLVideoElement {
        try {
            const video = document.createElement('video');
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.srcObject = mediaStream;

            return video;
        } catch (error) {
            console.error('Error creating video element:', error);
            throw error;
        }
    }

    private stopCapture(video: HTMLVideoElement): void {
        const tracks = (video.srcObject as MediaStream)?.getTracks();
        tracks?.forEach((track: MediaStreamTrack) => track.stop());
        video.srcObject = null;
        video.remove();
    }
}
